/**
 * Demo data for unauthenticated users.
 *
 * Shows realistic-looking but clearly fake data so visitors can
 * preview the dashboard without exposing real account information.
 */

import type {
  Account,
  Position,
  Order,
  TradeAnalysis,
  SecondOpinion,
  StrategistDecision,
  StrategistGateReview,
} from './AlpacaService';
import type { CalendarResponse } from '../components/TradeCalendar/calendarUtils';
import type { OverviewPayload, BotPeriodStats, PredictionTodayPayload } from './DashboardService';

const DEMO_STATS: BotPeriodStats = {
  total_trades: 42,
  wins: 27,
  losses: 15,
  win_rate: 64.3,
  total_pnl: 312.5,
  avg_win: 18.75,
  avg_loss: -10.2,
  profit_factor: 1.84,
  best_trade: 85.0,
  worst_trade: -32.0,
};

export const DEMO_ACCOUNT: Account = {
  equity: '50000.00',
  buying_power: '100000.00',
  cash: '50000.00',
  portfolio_value: '50000.00',
  last_equity: '49750.00',
  daily_pnl: '250.00',
  daily_pnl_pct: '0.50',
  status: 'ACTIVE',
  pattern_day_trader: false,
  daytrade_count: 1,
  currency: 'USD',
};

export const DEMO_POSITIONS: Position[] = [
  {
    symbol: 'SPY',
    qty: '10',
    side: 'long',
    avg_entry_price: '545.20',
    current_price: '547.80',
    market_value: '5478.00',
    unrealized_pl: '26.00',
    unrealized_plpc: '0.0048',
    change_today: '0.0031',
    asset_class: 'us_equity',
  },
  {
    symbol: 'NVDA',
    qty: '8',
    side: 'long',
    avg_entry_price: '118.50',
    current_price: '124.30',
    market_value: '994.40',
    unrealized_pl: '46.40',
    unrealized_plpc: '0.0489',
    change_today: '0.0215',
    asset_class: 'us_equity',
  },
  {
    symbol: 'QQQ',
    qty: '5',
    side: 'long',
    avg_entry_price: '478.50',
    current_price: '480.10',
    market_value: '2400.50',
    unrealized_pl: '8.00',
    unrealized_plpc: '0.0033',
    change_today: '0.0018',
    asset_class: 'us_equity',
  },
  {
    symbol: 'XLE',
    qty: '20',
    side: 'long',
    avg_entry_price: '62.10',
    current_price: '59.34',
    market_value: '1186.80',
    unrealized_pl: '-55.20',
    unrealized_plpc: '-0.0444',
    change_today: '-0.0152',
    asset_class: 'us_equity',
  },
  {
    symbol: 'TLT',
    qty: '15',
    side: 'long',
    avg_entry_price: '88.30',
    current_price: '88.75',
    market_value: '1331.25',
    unrealized_pl: '6.75',
    unrealized_plpc: '0.0051',
    change_today: '-0.0012',
    asset_class: 'us_equity',
  },
];

export const DEMO_ORDERS: Order[] = [
  {
    id: 'demo-001',
    symbol: 'SPY',
    side: 'buy',
    type: 'market',
    qty: '10',
    filled_qty: '10',
    filled_avg_price: '545.20',
    status: 'filled',
    submitted_at: new Date(Date.now() - 3600_000).toISOString(),
    filled_at: new Date(Date.now() - 3590_000).toISOString(),
    limit_price: null,
    stop_price: null,
    time_in_force: 'day',
  },
  {
    id: 'demo-002',
    symbol: 'QQQ',
    side: 'buy',
    type: 'limit',
    qty: '5',
    filled_qty: '5',
    filled_avg_price: '478.50',
    status: 'filled',
    submitted_at: new Date(Date.now() - 7200_000).toISOString(),
    filled_at: new Date(Date.now() - 7100_000).toISOString(),
    limit_price: '478.60',
    stop_price: null,
    time_in_force: 'day',
  },
];

export const DEMO_OVERVIEW: OverviewPayload = {
  date: new Date().toISOString().slice(0, 10),
  today_stats: DEMO_STATS,
  bots: {
    spy_share_scalper: {
      today: { ...DEMO_STATS, total_trades: 18, wins: 12, losses: 6, total_pnl: 145.3 },
      all_time: { ...DEMO_STATS, total_trades: 2400, wins: 1560, total_pnl: 8250.0 },
      last_trade: new Date(Date.now() - 300_000).toISOString(),
      has_data: true,
    },
    multi_stock_scalper: {
      today: { ...DEMO_STATS, total_trades: 14, wins: 9, losses: 5, total_pnl: 98.2 },
      all_time: { ...DEMO_STATS, total_trades: 1800, wins: 1080, total_pnl: 5120.0 },
      last_trade: new Date(Date.now() - 600_000).toISOString(),
      has_data: true,
    },
    tlt_scalp_bot: {
      today: { ...DEMO_STATS, total_trades: 10, wins: 6, losses: 4, total_pnl: 69.0 },
      all_time: { ...DEMO_STATS, total_trades: 1200, wins: 720, total_pnl: 3100.0 },
      last_trade: new Date(Date.now() - 900_000).toISOString(),
      has_data: true,
    },
  },
  prediction: null,
  updated_at: new Date().toISOString(),
};

export const DEMO_PREDICTION_TODAY: PredictionTodayPayload = {
  date: new Date().toISOString().slice(0, 10),
  generated_at: new Date().toISOString(),
  prediction: {
    direction: 'BULL',
    confidence: 0.72,
    raw_score: 5.4,
    max_score: 8.0,
    reasoning: [
      'VIX below 15 — low fear environment',
      'SPY gapped up +0.3% with volume',
      'News sentiment skews bullish (6 bullish / 2 bearish)',
    ],
    risk_flags: ['Earnings season — elevated single-stock vol'],
  },
  market_data: {
    vix_level: 14.2,
    spy_gap: { gap_pct: 0.3, prev_close: 544.5, current: 546.13 },
    spy_trend: { trend: 'uptrend' },
    news: { headline_count: 10, bullish_count: 6, bearish_count: 2, score: 0.4 },
    yesterday: {
      total_pnl: 285.0,
      total_trades: 38,
      win_rate: 63.2,
      worst_bot: 'tlt_scalp_bot',
      best_bot: 'spy_share_scalper',
    },
  },
  updated_at: new Date().toISOString(),
};

export const DEMO_TRADE_ANALYSIS: TradeAnalysis = {
  recommendation: 'BUY',
  confidence: 78,
  reasons: [
    'RSI(2) at 32.5 indicates oversold conditions after a 3-day pullback',
    'Strong institutional buying volume detected in the last 2 sessions',
    'Price holding above the 50-day moving average with bullish divergence on MACD',
  ],
  risk_factors: [
    'Broader market sentiment remains cautious with VIX above 18',
    'Earnings season volatility could cause sharp moves in either direction',
  ],
  suggested_stop_loss: 535.0,
  suggested_take_profit: 565.0,
  timing: 'Enter on next pullback to 543-544 range for better risk/reward',
  timing_horizon: 'days',
  rsi2: 32.5,
  rsi6: 40.1,
  rsi14: 48.2,
  mood_score: 15,
  geo_risk: 35,
  symbol: 'SPY',
  side: 'buy',
  price: 547.8,
};

export const DEMO_SECOND_OPINION: SecondOpinion = {
  verdict: 'PARTIALLY_AGREE',
  confidence: 72,
  summary: 'Technical setup supports a bounce, but macro headwinds limit upside potential.',
  reasoning: [
    'RSI oversold readings historically lead to 2-3 day bounces in SPY',
    'Volume profile confirms institutional accumulation at current levels',
    'Moving average support at 544 provides a clear risk management level',
  ],
  concerns: [
    'Fed rate decision next week could override any technical setup',
    'Earnings season adds unpredictable single-stock risk that could drag indices',
  ],
  would_take_trade: true,
  symbol: 'SPY',
  side: 'buy',
  model: 'gpt-4o',
};

export function getDemoStrategistGateReview(): StrategistGateReview {
  const now = Date.now();
  const runOne = new Date(now - 18 * 60_000).toISOString();
  const runTwo = new Date(now - 78 * 60_000).toISOString();

  return {
    count: 5,
    gates: ['risk_check', 'timing_confirm', 'liquidity_quality'],
    diagnostics: {
      logsScanned: 2,
      eventsScanned: 20,
      rejectionsScanned: 7,
      latestGateLogAt: runOne,
      latestGateLogSK: `STRAT_GATELOG#${runOne}`,
      hasGateLogs: true,
    },
    runs: [
      {
        SK: `STRAT_GATELOG#${runOne}`,
        runId: 'demo-run-latest',
        createdAt: runOne,
        totalEvents: 11,
        totalRejections: 4,
      },
      {
        SK: `STRAT_GATELOG#${runTwo}`,
        runId: 'demo-run-prior',
        createdAt: runTwo,
        totalEvents: 9,
        totalRejections: 3,
      },
    ],
    candidates: [
      {
        symbol: 'NVDA',
        runId: 'demo-run-latest',
        gateLogSK: `STRAT_GATELOG#${runOne}`,
        createdAt: runOne,
        lastGate: 'risk_check',
        reason: 'Position size would push portfolio tech exposure above the configured risk band.',
        rejectedAt: runOne,
        rejectedPrice: 921.1,
        direction: 'LONG',
        strategy: 'MOMENTUM_BREAKOUT',
        followUpDueAt: new Date(now + 42 * 60_000).toISOString(),
        followUpStatus: 'pending_outcome',
        passedGates: ['scanner_signal', 'analyst_review'],
        events: [
          {
            symbol: 'NVDA',
            gate: 'scanner_signal',
            result: 'PASSED',
            reason: 'Momentum breakout candidate with elevated relative volume.',
          },
          {
            symbol: 'NVDA',
            gate: 'analyst_review',
            result: 'PASSED',
            reason: 'Thesis quality was strong, but risk sizing needed validation.',
          },
          {
            symbol: 'NVDA',
            gate: 'risk_check',
            result: 'REJECTED',
            reason: 'Tech concentration and notional exposure were too high for this account state.',
          },
        ],
        rejections: [
          {
            gate: 'risk_check',
            reason: 'Tech concentration and notional exposure were too high for this account state.',
          },
        ],
        followUp: {
          outcome: 'later_winner',
          label: 'Later winner',
          detail: 'A later same-symbol strategist trade closed profitably.',
          decisionSK: 'DEMO_DECISION#NVDA#FOLLOWUP',
          action: 'BUY',
          createdAt: new Date(now + 42 * 60_000).toISOString(),
          closedAt: new Date(now + 128 * 60_000).toISOString(),
          hoursAfter: 1,
          pnlPct: 2.4,
          pnl: 184.5,
          entryPrice: 921.1,
          exitPrice: 943.2,
        },
      },
      {
        symbol: 'AMD',
        runId: 'demo-run-latest',
        gateLogSK: `STRAT_GATELOG#${runOne}`,
        createdAt: new Date(now - 22 * 60_000).toISOString(),
        lastGate: 'risk_check',
        reason: 'Correlated semiconductor setup while NVDA was already near the exposure limit.',
        rejectedAt: new Date(now - 22 * 60_000).toISOString(),
        rejectedPrice: 164.8,
        direction: 'LONG',
        strategy: 'SECTOR_LEADER',
        followUpDueAt: new Date(now + 38 * 60_000).toISOString(),
        followUpStatus: 'pending_outcome',
        passedGates: ['scanner_signal'],
        events: [],
        rejections: [
          {
            gate: 'risk_check',
            reason: 'Correlated semiconductor setup while NVDA was already near the exposure limit.',
          },
        ],
        followUp: {
          outcome: 'untracked',
          label: 'No later trade',
          detail: 'No same-symbol strategist trade was recorded within 72 hours.',
        },
      },
      {
        symbol: 'TSLA',
        runId: 'demo-run-latest',
        gateLogSK: `STRAT_GATELOG#${runOne}`,
        createdAt: new Date(now - 25 * 60_000).toISOString(),
        lastGate: 'timing_confirm',
        reason: 'Scanner fired before price reclaimed VWAP; wait for confirmation.',
        rejectedAt: new Date(now - 25 * 60_000).toISOString(),
        rejectedPrice: 188.4,
        direction: 'LONG',
        strategy: 'OVERSOLD_BOUNCE',
        followUpDueAt: new Date(now + 35 * 60_000).toISOString(),
        followUpStatus: 'pending_outcome',
        passedGates: ['scanner_signal', 'risk_check'],
        events: [
          {
            symbol: 'TSLA',
            gate: 'scanner_signal',
            result: 'PASSED',
            reason: 'Oversold bounce setup detected after a three-day pullback.',
          },
          {
            symbol: 'TSLA',
            gate: 'risk_check',
            result: 'PASSED',
            reason: 'Sizing fit inside the current portfolio guardrails.',
          },
          {
            symbol: 'TSLA',
            gate: 'timing_confirm',
            result: 'REJECTED',
            reason: 'Price remained below VWAP and spread widened during the entry window.',
          },
        ],
        rejections: [
          {
            gate: 'timing_confirm',
            reason: 'Price remained below VWAP and spread widened during the entry window.',
          },
        ],
        followUp: {
          outcome: 'later_loser',
          label: 'Later loser',
          detail: 'A later same-symbol strategist trade closed at a loss.',
          decisionSK: 'DEMO_DECISION#TSLA#FOLLOWUP',
          action: 'BUY',
          createdAt: new Date(now + 68 * 60_000).toISOString(),
          closedAt: new Date(now + 170 * 60_000).toISOString(),
          hoursAfter: 1.6,
          pnlPct: -1.1,
          pnl: -86.2,
          entryPrice: 188.4,
          exitPrice: 186.3,
        },
      },
      {
        symbol: 'TSLA',
        runId: 'demo-run-prior',
        gateLogSK: `STRAT_GATELOG#${runTwo}`,
        createdAt: runTwo,
        lastGate: 'timing_confirm',
        reason: 'Second timing failure in the sample; scanner may be early on this symbol.',
        rejectedAt: runTwo,
        rejectedPrice: 187.1,
        direction: 'LONG',
        strategy: 'MEAN_REVERSION',
        followUpDueAt: new Date(new Date(runTwo).getTime() + 60 * 60_000).toISOString(),
        followUpStatus: 'pending_outcome',
        passedGates: ['scanner_signal'],
        events: [
          {
            symbol: 'TSLA',
            gate: 'scanner_signal',
            result: 'PASSED',
            reason: 'Mean-reversion setup was detected.',
          },
          {
            symbol: 'TSLA',
            gate: 'timing_confirm',
            result: 'REJECTED',
            reason: 'Confirmation candle did not hold above the trigger level.',
          },
        ],
        rejections: [
          {
            gate: 'timing_confirm',
            reason: 'Confirmation candle did not hold above the trigger level.',
          },
        ],
        followUp: {
          outcome: 'pending',
          label: 'Later trade open',
          detail: 'A same-symbol strategist trade opened after this rejection and is still unresolved.',
          decisionSK: 'DEMO_DECISION#TSLA#OPEN',
          action: 'BUY',
          createdAt: new Date(now - 40 * 60_000).toISOString(),
          hoursAfter: 0.6,
          entryPrice: 187.1,
        },
      },
      {
        symbol: 'SMCI',
        runId: 'demo-run-prior',
        gateLogSK: `STRAT_GATELOG#${runTwo}`,
        createdAt: new Date(now - 92 * 60_000).toISOString(),
        lastGate: 'liquidity_quality',
        reason: 'Spread and volume quality were below the strategist threshold.',
        rejectedAt: new Date(now - 92 * 60_000).toISOString(),
        rejectedPrice: 872.5,
        direction: 'LONG',
        strategy: 'VOLUME_BREAKOUT',
        followUpDueAt: new Date(now - 32 * 60_000).toISOString(),
        followUpStatus: 'pending_outcome',
        passedGates: ['scanner_signal'],
        events: [
          {
            symbol: 'SMCI',
            gate: 'scanner_signal',
            result: 'PASSED',
            reason: 'Volume breakout screen found a potential continuation move.',
          },
          {
            symbol: 'SMCI',
            gate: 'liquidity_quality',
            result: 'REJECTED',
            reason: 'Spread widened beyond the configured quality guardrail.',
          },
        ],
        rejections: [
          {
            gate: 'liquidity_quality',
            reason: 'Spread widened beyond the configured quality guardrail.',
          },
        ],
        followUp: {
          outcome: 'later_loser',
          label: 'Later loser',
          detail: 'A later same-symbol strategist trade closed at a loss.',
          decisionSK: 'DEMO_DECISION#SMCI#FOLLOWUP',
          action: 'BUY',
          createdAt: new Date(now - 52 * 60_000).toISOString(),
          closedAt: new Date(now - 8 * 60_000).toISOString(),
          hoursAfter: 0.7,
          pnlPct: -1.6,
          pnl: -95.2,
          entryPrice: 872.5,
          exitPrice: 858.9,
        },
      },
    ],
  };
}

export function getDemoCalendarData(): CalendarResponse {
  const toDateKey = (offsetDays: number): string => {
    const date = new Date();
    date.setHours(12, 0, 0, 0);
    date.setDate(date.getDate() + offsetDays);
    return date.toISOString().slice(0, 10);
  };
  const atClose = (dateKey: string, hour: number): string => `${dateKey}T${hour.toString().padStart(2, '0')}:20:00Z`;
  const startDate = toDateKey(-45);
  const endDate = toDateKey(0);
  const recentWin = toDateKey(-2);
  const recentLoss = toDateKey(-4);
  const mixedDay = toDateKey(-7);
  const biggestWin = toDateKey(-9);

  return {
    startDate,
    endDate,
    days: [
      {
        date: biggestWin,
        netPnl: 286.4,
        tradeCount: 2,
        winCount: 2,
        lossCount: 0,
        trades: [
          {
            decisionSK: 'DEMO_DECISION#NVDA_WIN',
            symbol: 'NVDA',
            direction: 'long',
            entryPrice: 123.1,
            exitPrice: 127.35,
            pnl: 212.5,
            pnlPct: 3.45,
            qty: 50,
            strategy: 'MOMENTUM_BREAKOUT',
            closedAt: atClose(biggestWin, 19),
            reasoning: 'Momentum breakout held above VWAP and volume expanded through the decision window.',
          },
          {
            decisionSK: 'DEMO_DECISION#SPY_WIN',
            symbol: 'SPY',
            direction: 'long',
            entryPrice: 545.2,
            exitPrice: 546.68,
            pnl: 73.9,
            pnlPct: 0.27,
            qty: 50,
            strategy: 'VWAP_RECLAIM',
            closedAt: atClose(biggestWin, 18),
            reasoning: 'Index reclaimed VWAP while breadth improved, confirming risk-on follow-through.',
          },
        ],
      },
      {
        date: mixedDay,
        netPnl: 42.75,
        tradeCount: 3,
        winCount: 2,
        lossCount: 1,
        trades: [
          {
            decisionSK: 'DEMO_DECISION#TSLA_WIN',
            symbol: 'TSLA',
            direction: 'long',
            entryPrice: 183.4,
            exitPrice: 187.1,
            pnl: 111.0,
            pnlPct: 2.02,
            qty: 30,
            strategy: 'OVERSOLD_BOUNCE',
            closedAt: atClose(mixedDay, 20),
            reasoning: 'Oversold bounce confirmed after reclaiming the morning range.',
          },
          {
            decisionSK: 'DEMO_DECISION#AMD_WIN',
            symbol: 'AMD',
            direction: 'long',
            entryPrice: 158.2,
            exitPrice: 159.95,
            pnl: 35.0,
            pnlPct: 1.11,
            qty: 20,
            strategy: 'VWAP_RECLAIM',
            closedAt: atClose(mixedDay, 17),
            reasoning: 'VWAP reclaim worked, but semiconductor correlation kept size smaller.',
          },
          {
            decisionSK: 'DEMO_DECISION#XLE_LOSS',
            symbol: 'XLE',
            direction: 'short',
            entryPrice: 91.2,
            exitPrice: 93.15,
            pnl: -103.25,
            pnlPct: -2.14,
            qty: 53,
            strategy: 'MEAN_REVERSION',
            closedAt: atClose(mixedDay, 16),
            reasoning: 'Energy reversal failed as crude headlines pushed the sector through resistance.',
          },
        ],
      },
      {
        date: recentLoss,
        netPnl: -148.2,
        tradeCount: 2,
        winCount: 0,
        lossCount: 2,
        trades: [
          {
            decisionSK: 'DEMO_DECISION#SMCI_LOSS',
            symbol: 'SMCI',
            direction: 'long',
            entryPrice: 872.5,
            exitPrice: 858.9,
            pnl: -95.2,
            pnlPct: -1.56,
            qty: 7,
            strategy: 'VOLUME_BREAKOUT',
            closedAt: atClose(recentLoss, 19),
            reasoning: 'Breakout trigger fired before liquidity quality improved; spread widened into entry.',
          },
          {
            decisionSK: 'DEMO_DECISION#QQQ_LOSS',
            symbol: 'QQQ',
            direction: 'long',
            entryPrice: 478.5,
            exitPrice: 477.44,
            pnl: -53.0,
            pnlPct: -0.22,
            qty: 50,
            strategy: 'MOMENTUM_BREAKOUT',
            closedAt: atClose(recentLoss, 18),
            reasoning: 'Market breadth faded after entry, invalidating the continuation thesis.',
          },
        ],
      },
      {
        date: recentWin,
        netPnl: 96.8,
        tradeCount: 1,
        winCount: 1,
        lossCount: 0,
        trades: [
          {
            decisionSK: 'DEMO_DECISION#AAPL_WIN',
            symbol: 'AAPL',
            direction: 'long',
            entryPrice: 191.3,
            exitPrice: 194.08,
            pnl: 96.8,
            pnlPct: 1.45,
            qty: 35,
            strategy: 'BB_SQUEEZE',
            closedAt: atClose(recentWin, 19),
            reasoning: 'Compression resolved higher with clean risk and no conflicting sector exposure.',
          },
        ],
      },
    ],
  };
}

export function getDemoStrategistDecisions(): StrategistDecision[] {
  const calendar = getDemoCalendarData();

  return calendar.days.flatMap((day) =>
    day.trades.map((trade, index) => ({
      PK: 'USER#demo',
      SK: trade.decisionSK || `DEMO_DECISION#${day.date}#${index}`,
      action: trade.direction === 'short' ? 'SELL_SHORT' : 'BUY',
      symbol: trade.symbol,
      qty: trade.qty,
      reasoning: trade.reasoning,
      confidence: trade.pnl >= 0 ? 78 : 61,
      urgency: 'medium',
      entryPrice: trade.entryPrice,
      filledPrice: trade.entryPrice,
      status: trade.pnl >= 0 ? 'closed_profit' : 'closed_loss',
      exitPrice: trade.exitPrice,
      pnl: trade.pnl,
      pnlPct: trade.pnlPct,
      closedAt: trade.closedAt,
      createdAt: new Date(new Date(trade.closedAt).getTime() - 90 * 60_000).toISOString(),
      strategy: trade.strategy,
      direction: trade.direction,
      source: 'demo',
    })),
  );
}
